/*
 * `cn` — the class-merging helper every @ds component uses.
 *
 * Two jobs, in order:
 *   1. clsx flattens conditionals/arrays/objects into a class string.
 *   2. tailwind-merge drops earlier classes that conflict with later ones, so a
 *      caller's `class` prop can override a component's defaults.
 *
 * Step 2 is the load-bearing one. Without it, `<DSButton class="rounded-full">`
 * emits both `rounded-ds-md` (the component default) and `rounded-full`, and
 * whichever CSS rule happens to come later wins — meaning the prop silently
 * fails to override roughly half the time.
 *
 * WHY THE CONFIG IS EXTENDED
 *
 * tailwind-merge decides conflicts from a table of known class groups built
 * around Tailwind's *default* scales. Our @ds scales are namespaced —
 * `rounded-ds-md`, `shadow-ds-lg` — precisely so they do not collide with the
 * app's legacy utilities (see tailwind.ds.js). tailwind-merge has no way to know
 * that, so out of the box it treats them as unrelated classes and merges
 * nothing:
 *
 *   twMerge('rounded-ds-md', 'rounded-ds-lg')  ->  'rounded-ds-md rounded-ds-lg'
 *   twMerge('rounded-ds-md', 'rounded-full')   ->  'rounded-ds-md rounded-full'
 *   twMerge('shadow-ds-sm',  'shadow-none')    ->  'shadow-ds-sm shadow-none'
 *
 * Registering the namespaced values into the existing groups fixes all three, and
 * keeps them conflicting with Tailwind's own values too (`rounded-full` beats
 * `rounded-ds-md`, as a reader would expect).
 *
 * KEEP IN SYNC: the value lists below mirror `borderRadius` and `boxShadow` in
 * tailwind.ds.js. Adding a step there without adding it here produces a subtle,
 * silent override bug rather than an error — the unit test in
 * src/tests/unit/cn.spec.ts guards the pairs that matter.
 */
import { clsx, type ClassValue } from 'clsx'
import { extendTailwindMerge } from 'tailwind-merge'

/** Namespaced radius steps — mirrors `borderRadius` in tailwind.ds.js. */
const DS_RADIUS = ['ds-sm', 'ds-md', 'ds-lg', 'ds-xl', 'ds-2xl']

/** Namespaced shadow steps — mirrors `boxShadow` in tailwind.ds.js. */
const DS_SHADOW = ['ds-xs', 'ds-sm', 'ds-md', 'ds-lg', 'ds-xl', 'ds-focus']

/** Namespaced font-family keys — mirrors `fontFamily` in tailwind.ds.js. */
const DS_FONT_FAMILY = ['ds', 'ds-caption']

/*
 * Tailwind generates per-side and per-corner variants from every borderRadius
 * key, so `rounded-t-ds-md` and `rounded-tl-ds-md` are real utilities and need
 * registering alongside the all-corners group. These are tailwind-merge's own
 * group ids, taken from getDefaultConfig().classGroups.
 */
const RADIUS_GROUP_IDS = [
  'rounded',
  'rounded-s',
  'rounded-e',
  'rounded-t',
  'rounded-r',
  'rounded-b',
  'rounded-l',
  'rounded-ss',
  'rounded-se',
  'rounded-ee',
  'rounded-es',
  'rounded-tl',
  'rounded-tr',
  'rounded-br',
  'rounded-bl',
] as const

const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      // One entry per radius group: { 'rounded-t': ['ds-sm', ...] } and so on.
      // Built rather than spelled out because it is the same list 15 times; the
      // cast is needed only because the keys are computed.
      ...(Object.fromEntries(
        RADIUS_GROUP_IDS.map(id => [id, [{ [id]: DS_RADIUS }]]),
      ) as Record<string, [Record<string, string[]>]>),
      shadow: [{ shadow: DS_SHADOW }],
      'font-family': [{ font: DS_FONT_FAMILY }],
    },
  },
})

/**
 * Merge class names, resolving Tailwind conflicts in favour of the last one.
 * Use in every @ds component so a `class` prop reliably overrides defaults.
 */
export const cn = (...inputs: ClassValue[]): string => twMerge(clsx(inputs))
